import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, Phone, Mail, Car, MapPin, Clock, Inbox, Loader2, RefreshCw, Euro, Navigation } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LocationMap from "@/components/LocationMap";
import { api, fileUrl, eur, STATUS_META } from "@/lib/api";

const FILTERS = [
  { key: "alle", label: "Alle" },
  { key: "nieuw", label: "Nieuw" },
  { key: "in_behandeling", label: "In behandeling" },
  { key: "voltooid", label: "Voltooid" },
];

const Dashboard = () => {
  const [stats, setStats] = useState({ total: 0, nieuw: 0, in_behandeling: 0, voltooid_vandaag: 0 });
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("alle");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/requests", { params: { status: filter, q: q || undefined } }),
        api.get("/stats"),
      ]);
      setRequests(r.data);
      setStats(s.data);
    } catch (e) {
      toast.error("Laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [filter, q]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      const res = await api.patch(`/requests/${id}/status`, { status });
      setRequests((prev) => prev.map((x) => (x.id === id ? res.data : x)));
      setSelected((s) => (s && s.id === id ? res.data : s));
      const stat = await api.get("/stats");
      setStats(stat.data);
      toast.success("Status bijgewerkt");
    } catch (e) {
      toast.error("Bijwerken mislukt");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Dispatcher Dashboard</h1>
          <p className="text-slate-400 text-sm mt-1">Beheer inkomende takelverzoeken</p>
        </div>
        <Button onClick={load} data-testid="refresh-btn" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800 gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Vernieuwen
        </Button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi testid="kpi-total" label="Totaal aanvragen" value={stats.total} color="#38BDF8" />
        <Kpi testid="kpi-nieuw" label="Nieuw (wachtend)" value={stats.nieuw} color="#F59E0B" />
        <Kpi testid="kpi-inbehandeling" label="In behandeling" value={stats.in_behandeling} color="#0EA5E9" />
        <Kpi testid="kpi-voltooid" label="Voltooid vandaag" value={stats.voltooid_vandaag} color="#10B981" />
      </div>

      {/* Filters + search */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op naam, kenteken of telefoon..."
            className="pl-9 bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === f.key ? "bg-sky-500 text-slate-950" : "bg-slate-800/60 text-slate-300 hover:bg-slate-700"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-sky-400 animate-spin" /></div>
      ) : requests.length === 0 ? (
        <div className="text-center py-20 text-slate-500" data-testid="empty-state">
          <Inbox className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Geen takelverzoeken gevonden.</p>
        </div>
      ) : (
        <div className="grid gap-4" data-testid="requests-list">
          {requests.map((r) => (
            <RequestCard key={r.id} r={r} onOpen={() => setSelected(r)} onStatus={changeStatus} />
          ))}
        </div>
      )}

      <DetailDialog req={selected} onClose={() => setSelected(null)} onStatus={changeStatus} />
    </div>
  );
};

const Kpi = ({ label, value, color, testid }) => (
  <div data-testid={testid} className="rounded-2xl bg-slate-900/60 border border-slate-800 p-5">
    <div className="text-3xl font-heading font-extrabold" style={{ color }}>{value}</div>
    <div className="text-sm text-slate-400 mt-1">{label}</div>
  </div>
);

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.nieuw;
  return <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ color: m.color, background: m.bg }}>{m.label}</span>;
};

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString("nl-NL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const RequestCard = ({ r, onOpen, onStatus }) => (
  <div data-testid={`request-card-${r.id}`} className="rounded-2xl bg-slate-900/60 border border-slate-800 hover:border-sky-500/40 transition-colors p-5">
    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
      {r.photos?.[0] && (
        <img src={fileUrl(r.photos[0].storage_path)} alt="voertuig" className="w-full lg:w-24 h-24 object-cover rounded-xl border border-slate-700" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="font-heading font-semibold text-white text-lg">{r.customer_name}</h3>
          <StatusBadge status={r.status} />
          <span className="text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" /> {fmt(r.created_at)}</span>
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-slate-400">
          <span className="flex items-center gap-1.5"><Car className="w-4 h-4 text-sky-400" /> {r.vehicle_model} · {r.license_plate}</span>
          <span className="flex items-center gap-1.5"><Phone className="w-4 h-4 text-sky-400" /> {r.phone}</span>
          {r.address && <span className="flex items-center gap-1.5 truncate max-w-[220px]"><MapPin className="w-4 h-4 text-sky-400" /> {r.address}</span>}
          {r.price && <span className="flex items-center gap-1.5 text-sky-400 font-semibold"><Euro className="w-4 h-4" /> {eur(r.price.total_incl ?? r.price.total)} <span className="text-slate-500 font-normal">incl. btw</span></span>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <StatusSelect value={r.status} onChange={(v) => onStatus(r.id, v)} testid={`status-select-${r.id}`} />
        <Button onClick={onOpen} data-testid={`open-detail-${r.id}`} className="bg-sky-500/15 text-sky-300 hover:bg-sky-500/25 border border-sky-500/30">Details</Button>
      </div>
    </div>
  </div>
);

const StatusSelect = ({ value, onChange, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="w-[160px] bg-slate-950/50 border-slate-700 text-white"><SelectValue /></SelectTrigger>
    <SelectContent className="bg-slate-900 border-slate-700 text-white">
      <SelectItem value="nieuw">Nieuw</SelectItem>
      <SelectItem value="in_behandeling">In behandeling</SelectItem>
      <SelectItem value="voltooid">Voltooid</SelectItem>
    </SelectContent>
  </Select>
);

const DetailDialog = ({ req, onClose, onStatus }) => {
  if (!req) return null;
  return (
    <Dialog open={!!req} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="detail-dialog" className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-3">
            {req.customer_name} <StatusBadge status={req.status} />
          </DialogTitle>
          <DialogDescription className="text-slate-400">Details van het takelverzoek</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Info icon={Phone} label="Telefoon" value={req.phone} />
            <Info icon={Mail} label="E-mail" value={req.email} />
            {req.customer_address && <Info icon={MapPin} label="Adres klant" value={req.customer_address} />}
            <Info icon={Car} label="Voertuig" value={`${req.vehicle_model} · ${req.license_plate}`} />
            <Info icon={Clock} label="Ingediend" value={fmt(req.created_at)} />
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Probleem</div>
            <p className="text-slate-200 text-sm bg-slate-950/50 rounded-lg p-3 border border-slate-800">{req.problem_description}</p>
          </div>

          {req.photos?.length > 0 && (
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wide mb-2">Foto's</div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {req.photos.map((p, i) => (
                  <a key={p.storage_path} href={fileUrl(p.storage_path)} target="_blank" rel="noreferrer" className="aspect-square rounded-lg overflow-hidden border border-slate-700">
                    <img src={fileUrl(p.storage_path)} alt={p.original_filename} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-sky-400" /> Ophaallocatie</div>
              {req.address && <p className="text-slate-300 text-sm mb-2">{req.address}</p>}
              {req.lat != null ? <LocationMap lat={req.lat} lng={req.lng} interactive={false} height="180px" /> : <p className="text-slate-500 text-sm">Niet opgegeven.</p>}
            </div>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1.5"><Navigation className="w-3.5 h-3.5 text-emerald-400" /> Bestemming</div>
              {req.dest_address && <p className="text-slate-300 text-sm mb-2">{req.dest_address}</p>}
              {req.dest_lat != null ? <LocationMap lat={req.dest_lat} lng={req.dest_lng} interactive={false} height="180px" /> : <p className="text-slate-500 text-sm">Niet opgegeven.</p>}
            </div>
          </div>

          {req.price && (
            <div className="rounded-xl bg-sky-500/5 border border-sky-500/30 p-4" data-testid="detail-price">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-slate-500 uppercase tracking-wide">Prijsberekening — {req.price.service_label}</span>
                {req.price.night_weekend && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400">Nacht/weekend</span>}
              </div>
              <div className="space-y-1 text-sm">
                <DRow label="Voorrijkosten" value={eur(req.price.base_callout)} />
                <DRow label={`Afstand ${req.price.distance_km} km (voorrijden ${req.price.approach_km} + takelen ${req.price.tow_km})`} value={eur(req.price.km_cost)} />
                {req.price.surcharge > 0 && <DRow label="Toeslag dienst" value={eur(req.price.surcharge)} />}
                {req.price.night_weekend && <DRow label="Nacht-/weekendtoeslag" value={eur(req.price.night_extra)} />}
                <DRow label="Subtotaal (excl. btw)" value={eur(req.price.total)} />
                <DRow label="Btw (21%)" value={eur(req.price.vat ?? 0)} />
                <div className="flex justify-between pt-1.5 mt-1 border-t border-sky-500/30 font-bold text-white"><span>Totaal (incl. btw)</span><span className="text-sky-400">{eur(req.price.total_incl ?? req.price.total)}</span></div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2 border-t border-slate-800">
            <span className="text-sm text-slate-400">Status wijzigen:</span>
            <StatusSelect value={req.status} onChange={(v) => onStatus(req.id, v)} testid="detail-status-select" />
            <a href={`tel:${req.phone}`} className="ml-auto">
              <Button data-testid="call-customer-btn" className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold gap-2"><Phone className="w-4 h-4" /> Bel klant</Button>
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Info = ({ icon: Icon, label, value }) => (
  <div>
    <div className="text-xs text-slate-500 uppercase tracking-wide mb-0.5 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {label}</div>
    <div className="text-slate-200">{value}</div>
  </div>
);

const DRow = ({ label, value }) => (
  <div className="flex justify-between gap-4"><span className="text-slate-400">{label}</span><span className="text-slate-200 whitespace-nowrap">{value}</span></div>
);

export default Dashboard;
